"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const PROGRESS_CAP = 94;
const FINISH_MIN_MS = 400;
const FINISH_TICK_MS = 60;

function progressSpeedFactor(elapsedMs: number): number {
  if (elapsedMs >= 20_000) return 0.12;
  if (elapsedMs >= 10_000) return 0.3;
  if (elapsedMs >= 5_000) return 0.55;
  return 1;
}

function timeBasedTarget(elapsedMs: number): number {
  const tau =
    elapsedMs >= 15_000 ? 28_000 : elapsedMs >= 8_000 ? 18_000 : 12_000;
  return PROGRESS_CAP * (1 - Math.exp(-elapsedMs / tau));
}

/**
 * Indeterminate progress that asymptotically approaches ~94% while work runs,
 * then eases to 100% when `complete()` is called. Used for tool-run UX.
 */
export function useSimulatedProgress() {
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  const finishResolveRef = useRef<(() => void) | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    finishResolveRef.current = null;
    setProgress(0);
    setActive(false);
  }, [clearTimer]);

  const start = useCallback(() => {
    clearTimer();
    startedAtRef.current = Date.now();
    setActive(true);
    setProgress(4);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      const target = timeBasedTarget(elapsed);
      const speed = progressSpeedFactor(elapsed);

      setProgress((value) => {
        /* istanbul ignore next -- the curve approaches the cap asymptotically, never reaching it */
        if (value >= PROGRESS_CAP) return value;
        const desired = Math.max(value, target);
        const gap = desired - value;
        if (gap <= 0.05) return value;
        const step = Math.min(gap, (0.35 + Math.random() * 0.45) * speed);
        return Math.min(PROGRESS_CAP, value + step);
      });
    }, 120);
  }, [clearTimer]);

  const complete = useCallback((): Promise<void> => {
    clearTimer();
    const finishStartedAt = Date.now();

    return new Promise((resolve) => {
      finishResolveRef.current = resolve;

      timerRef.current = setInterval(() => {
        setProgress((value) => {
          if (value >= 100) {
            clearTimer();
            finishResolveRef.current?.();
            finishResolveRef.current = null;
            return 100;
          }

          const elapsed = Date.now() - finishStartedAt;
          const gap = 100 - value;

          if (gap <= 0.15) {
            if (elapsed >= FINISH_MIN_MS) {
              clearTimer();
              finishResolveRef.current?.();
              finishResolveRef.current = null;
              return 100;
            }
            /* istanbul ignore next -- progress is capped below 100 before `complete()` runs */
            return value;
          }

          const step = Math.min(gap, Math.max(2, gap * 0.22));
          return value + step;
        });
      }, FINISH_TICK_MS);
    });
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { progress, active, start, complete, reset };
}
